import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {ConfigService} from './config.service';
import {Observable, throwError} from 'rxjs';
import {catchError, map} from 'rxjs/operators';

import {BaseModel, QueryResultApi, ResponseApiModel} from '../models';


export class ApiService<T extends BaseModel> {
  protected dataKey: string = null;
  protected objectKey: string = null;
  protected sendObjectKey: string = null;
  protected reciveObjectKey: string = null;
  protected sendObjectIdsKey: string = null;

  constructor(protected ctor: new () => T, protected httpClient: HttpClient, protected urlKey = 'apiNucleoUrl') {
  }

  get(params = null): Observable<QueryResultApi<T>> {
    return this.getItems('query', this.dataKey, params);
  }

  /**
   * Get list of elemnts.
   * @param method to call
   * @param dataKey Key for data in response
   * @param params params for request
   * @param addObject add object to url
   */
  getItems(method: string, dataKey: string, params = null, addObject = true): Observable<QueryResultApi<T>> {
    const url = this.getUrl(method, addObject);
    return this.httpClient.post<{ queryResults: ResponseApiModel }>(url, params)
      .pipe(
        map(results => results.queryResults),
        map(response => {

          const result = new QueryResultApi<T>();
          result.items = [];
          if (response.success) {
            const items: T[] = [];
            if (dataKey in response) {
              response[dataKey].forEach((data: any) => {
                const model = new this.ctor();
                items.push({...model, ...data});
              });
            }
            result.items = items;

            if(response.meta)
              result.meta = response.meta;
          } else {
            let description = `Error al obtener los ${this.dataKey}`;
            if (response.error && response.error.description) {
              description = response.error.description;
            }
            throw throwError(description);
          }

          result.success = response.success;
          return result;

        }),
        catchError(
          this.handleError
        )
      );
  }

  /**
   * Insert new element.
   * @param item Data to insert.
   * @param params
   */
  add(item: T, params = null): Observable<T> {
    const url = this.getUrl('add', true);
    const body = params ? {...params} : {};
    const itemNoNull = this.removeNull(item);
    body[this.sendObjectKey] = [{...itemNoNull}];
    return this.httpClient.post<{ addResults: ResponseApiModel[] }>(url, body)
      .pipe(
        map(results => results.addResults),
        map((response: ResponseApiModel[]) => this.processModifyResult(response)),
        catchError(this.handleError)
      );
  }

  /**
   * Insert new element.
   * @param items Data to insert.
   * @param params
   * @param rNull
   */
  addMany(items: T[], params = null, rNull = true): Observable<{ items: T[], errors: string[] }> {
    const url = this.getUrl('add', true);
    const body = params ? {...params} : {};
    body[this.sendObjectKey] = rNull ? items.map(i => this.removeNull(i)) : items;
    return this.httpClient.post<{ addResults: ResponseApiModel[] }>(url, body)
      .pipe(
        map(results => results.addResults),
        map((response: ResponseApiModel[]) => this.processModifyResultMany(response)),
        catchError(this.handleError)
      );
  }

  /**
   * Modify element.
   * @param item Data to send.
   * @param params
   */
  update(item: T, params = null): Observable<T> {
    const url = this.getUrl('update', true);
    const body = params ? {...params} : {};
    const itemNoNull = this.removeNull(item);
    body[this.sendObjectKey] = [{...itemNoNull}];
    return this.httpClient.post<{ updateResults: ResponseApiModel[] }>(url, body)
      .pipe(
        map(results => results.updateResults),
        map((response: ResponseApiModel[]) => this.processModifyResult(response)),
        catchError(this.handleError)
      );
  }

  /**
   * Modify element.
   * @param items
   * @param params
   * @param rNull
   */
  updateMany(items: T[], params = null, rNull = true): Observable<{ items: T[], errors: string[] }> {
    const url = this.getUrl('update', true);
    const body = params ? {...params} : {};
    body[this.sendObjectKey] = rNull ? items.map(i => this.removeNull(i)) : items;
    return this.httpClient.post<{ updateResults: ResponseApiModel[] }>(url, body)
      .pipe(
        map(results => results.updateResults),
        map((response: ResponseApiModel[]) => this.processModifyResultMany(response)),
        catchError(this.handleError)
      );
  }

  /**
   * Remove element.
   * @param id The identify element for delete.
   */
  delete(id: string): Observable<boolean> {
    const url = this.getUrl('delete', true);
    const body = {};
    body[this.sendObjectIdsKey] = [id];
    return this.httpClient.post<{ deleteResults: ResponseApiModel[] }>(url, body)
      .pipe(
        map(results => results.deleteResults),
        map(queryResult => {
          let success = false;
          if (queryResult.length > 0) {
            const result = queryResult[0];
            success = result.success;
            if (!success) {
              throw throwError(result.error.description);
            }
          } else {
            throw throwError(`Error al eliminar el objeto ${this.objectKey}.`);
          }
          return success;
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Remove element.
   * @param ids The identify elements for delete.
   */
  deleteMany(ids: string[]): Observable<{ items: string[], errors: string[] }> {
    const url = this.getUrl('delete', true);
    const body = {};
    body[this.sendObjectIdsKey] = ids;
    return this.httpClient.post<{ deleteResults: ResponseApiModel[] }>(url, body)
      .pipe(
        map(results => results.deleteResults),
        map(response => {

          const items: string[] = [];
          const errors = [];
          if (response.length > 0) {
            for (const result of response) {
              if (result.success) {
                items.push(result.id);
              }
              else{
                errors.push(result.error.description);
              }
            }
          } else {
            throw throwError([`Error al procesar los datos recibidos de la petición.`]);
          }

          return {items, errors};
        }),
        catchError(this.handleError)
      );
  }


  /**
   * Verify and transform response from update element.
   * @param response Response from update element.
   */
  protected processModifyResult(response: ResponseApiModel[]): T {
    let item: T = new this.ctor();
    if (response.length > 0) {
      const result = response[0];
      item = this.saveErrorsAndWarnings(item, result);
      if (result.success) {
        if (this.reciveObjectKey in result) {
          const data = result[this.reciveObjectKey];
          item = {...item, ...data};
        } else {
          throw throwError(`No se encuentra la llave ${this.reciveObjectKey} en los datos recibidos.`);
        }
      } else {
        let error: any = result.error.description;
        const itemAux: any = this.getItemWithOnlyErrorsAndWarnings(item);
        if ((itemAux.errors && itemAux.errors.length > 0) || (itemAux.warnings && itemAux.warnings.length > 0)) {
          error = {error, item: itemAux};
        }
        throw throwError(error);
      }
    } else {
      throw throwError(`Error al procesar los datos recibidos de la petición.`);
    }

    return item;
  }


  /**
   * Verify and transform response from update element.
   * @param response Response from update element.
   */
  protected processModifyResultMany(response: ResponseApiModel[]): { items: T[], errors: string[] } {
    const items: T[] = [];
    const errors = [];
    if (response.length > 0) {
      for (const result of response) {
        let item: T = new this.ctor();
        item = this.saveErrorsAndWarnings(item, result);
        if (this.reciveObjectKey in result) {
          const data = result[this.reciveObjectKey];
          items.push({...item, ...data});
        }
        if (!result.success) {
          errors.push(result.error.description);
        }
      }
    } else {
      throw throwError(`Error al procesar los datos recibidos de la petición.`);
    }

    return {items, errors};
  }

  /**
   * Extraer resultado de la respuesta.
   * @param response Response from update element.
   * @param resultKey llave a extraer
   * @param defaultValue valor por defecto, si no esta y la llave no se encuentra lanza error
   */
  protected extractResult(response: ResponseApiModel, resultKey: string, defaultValue: any | undefined) {
    let result = defaultValue;
    if (resultKey in response) {
      result = response[resultKey];
    } else {
      if (defaultValue === undefined) {
        throw throwError(`El valo ${resultKey} no se encuentra en la respuesta.`);
      }
    }
    return result;
  }

  /**
   * Return url from method.
   * @param method to url.
   * @param addObject agregar el objectKey al final.
   */
  protected getUrl(method: string, addObject = false): string {
    const objectKey = addObject ? this.objectKey : '';
    const baseApiUrl = ConfigService.get(this.urlKey);
    return `${baseApiUrl}/${method}${objectKey}`;
  }


  /**
   * Throw error for response
   * @param error Response with error.
   */
  protected handleError(error: HttpErrorResponse): Observable<never> {
    let result;
    if (error instanceof Observable) {
      result = error;
    } else {
      let errorMsg = 'Error desconocido';
      if (error.error instanceof ErrorEvent) {
        errorMsg = `Error: ${error.error.message}`;
      } else {
        if ('message' in error) {
          errorMsg = error.message;
          if ('name' in error) {
            switch (error.name) {
              case "HttpErrorResponse":
                errorMsg = 'Error en la solicitud';
                if ('url' in error) {
                  errorMsg = `${errorMsg}: ${error.url}`;
                }
                break;
            }
          }
        }
      }
      result = throwError(errorMsg);
    }
    return result;
  }

  /**
   * Eliminar los atributos que tiene valor null,
   * Tomado de https://stackoverflow.com/questions/286141/remove-blank-attributes-from-an-object-in-javascript
   * @param obj
   * @protected
   */
  protected removeNull(obj): any {
    if (Array.isArray(obj)) {
      return obj
        .map(v => (v && typeof v === 'object') ? this.removeNull(v) : v)
        .filter(v => !(v == null));
    } else {
      return Object.entries(obj)
        .map(([k, v]) => [k, v && typeof v === 'object' ? this.removeNull(v) : v])
        .reduce((a, [k, v]) => (v == null ? a : (a[k] = v, a)), {});
    }
  }

  protected saveErrorsAndWarnings(item: any, result: any): T {
    if (result.errors) {
      item.errors = result.errors;
    }
    if (result.warnings) {
      item.warnings = result.warnings;
    }
    return item;
  }

  protected getItemWithOnlyErrorsAndWarnings(item: any): any {
    return {
      errors: item.errors,
      warnings: item.warnings,
    };
  }

  setKeysBy(item: any){}
}
